import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const PLANS = [
  { days: 1,  daily: 0.005 },
  { days: 5,  daily: 0.007 },
  { days: 10, daily: 0.008 },
  { days: 15, daily: 0.009 },
  { days: 20, daily: 0.010 },
  { days: 25, daily: 0.011 },
  { days: 30, daily: 0.012 },
];

const MIN = 5;
const MAX = 100000;
const FEE_BPS = 0.10;

const formatUSD = (n) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

const CalculatorSection = () => {
  const { t } = useTranslation();
  const [amountStr, setAmountStr] = useState('1000');
  const [planIdx, setPlanIdx] = useState(6); // 30-day default

  const plan = PLANS[planIdx];
  const amountNum = Math.max(MIN, Math.min(MAX, Number(amountStr) || MIN));

  const result = useMemo(() => {
    const dailyProfit = amountNum * plan.daily;
    const gross = dailyProfit * plan.days;
    const fee = gross * FEE_BPS;
    const net = gross - fee;
    const total = amountNum + net;
    return { dailyProfit, gross, fee, net, total };
  }, [amountNum, plan]);

  const handleAmountChange = (e) => {
    setAmountStr(e.target.value);
  };

  const handleAmountBlur = () => {
    setAmountStr(String(amountNum));
  };

  const sliderProgress =
    ((Math.log(amountNum) - Math.log(MIN)) / (Math.log(MAX) - Math.log(MIN))) * 100;

  const handleSliderChange = (e) => {
    const pct = Number(e.target.value) / 100;
    const logMin = Math.log(MIN);
    const logMax = Math.log(MAX);
    const v = Math.exp(logMin + (logMax - logMin) * pct);
    setAmountStr(String(Math.round(v)));
  };

  return (
    <section id="calculator" className="section-x calculator">
      <div className="dot-grid dot-grid--subtle" />
      <div className="gold-orb gold-orb--primary calculator__orb" />

      <div className="container">
        <div className="row">
          <div className="col-lg-9 mx-auto text-center mb-5">
            <div className="eyebrow eyebrow--center mb-3" data-aos="fade-up">
              <span>{t('calculator.eyebrow')}</span>
            </div>
            <h2 className="section-heading" data-aos="fade-up">
              {t('calculator.titleLead')}{' '}
              <span className="gold-text">{t('calculator.titleHighlight')}</span>
            </h2>
            <p className="section-subheading" data-aos="fade-up" data-aos-delay="80">
              {t('calculator.subtitle')}
            </p>
          </div>
        </div>

        <div className="row g-4 align-items-stretch">
          <div className="col-lg-6" data-aos="fade-right">
            <div className="calc-card">
              <div className="calc-card__header">
                <span className="sf-badge sf-badge--outline">{t('calculator.inputs')}</span>
              </div>

              <div className="calc-field">
                <label className="calc-field__label">{t('calculator.amount')}</label>
                <div className="calc-field__input-wrap">
                  <span className="calc-field__prefix">$</span>
                  <input
                    type="number"
                    className="calc-field__input mono tabular-nums"
                    min={MIN}
                    max={MAX}
                    value={amountStr}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                  />
                  <span className="calc-field__suffix">USDT</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={sliderProgress}
                  onChange={handleSliderChange}
                  className="calc-slider"
                  style={{ '--progress': `${sliderProgress}%` }}
                />
                <div className="calc-field__range">
                  <span>$5</span>
                  <span>$1,000</span>
                  <span>$10,000</span>
                  <span>$100,000</span>
                </div>
              </div>

              <div className="calc-field">
                <label className="calc-field__label">{t('calculator.plan')}</label>
                <div className="calc-plan-grid">
                  {PLANS.map((p, i) => (
                    <button
                      key={p.days}
                      type="button"
                      className={`calc-plan ${planIdx === i ? 'is-active' : ''}`}
                      onClick={() => setPlanIdx(i)}
                    >
                      <span className="calc-plan__days mono tabular-nums">{p.days}</span>
                      <span className="calc-plan__unit">{t('strategies.days')}</span>
                      <span className="calc-plan__rate mono">{(p.daily * 100).toFixed(1)}%/d</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6" data-aos="fade-left">
            <div className="calc-card calc-card--result">
              <div className="calc-card__header">
                <span className="sf-badge sf-badge--gold">{t('calculator.result')}</span>
                <span className="calc-card__headnote mono">
                  {amountNum.toLocaleString()} USDT · {plan.days} {t('strategies.days')} · {(plan.daily * 100).toFixed(1)}%/d
                </span>
              </div>

              <div className="calc-result">
                <div className="calc-result__row">
                  <span>{t('calculator.dailyProfit')}</span>
                  <span className="mono tabular-nums">{formatUSD(result.dailyProfit)}</span>
                </div>
                <div className="calc-result__row">
                  <span>{t('calculator.gross')} ({plan.days}d)</span>
                  <span className="mono tabular-nums">{formatUSD(result.gross)}</span>
                </div>
                <div className="calc-result__row calc-result__row--fee">
                  <span>{t('calculator.fee')} (10%)</span>
                  <span className="mono tabular-nums">− {formatUSD(result.fee)}</span>
                </div>
                <div className="calc-result__row calc-result__row--net">
                  <span>{t('calculator.net')}</span>
                  <span className="mono tabular-nums gold-text">{formatUSD(result.net)}</span>
                </div>
                <div className="calc-result__divider" />
                <div className="calc-result__row calc-result__row--total">
                  <span>{t('calculator.total')}</span>
                  <span className="mono tabular-nums">{formatUSD(result.total)}</span>
                </div>
              </div>

              <p className="calc-card__footnote">
                <i className="fas fa-info-circle" /> {t('calculator.footnote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;
